import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, FileSpreadsheet, FileText, CheckCircle, XCircle, 
  Download, Trash, AlertCircle, ArrowRight, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { uploadFileToGCS } from '../utils/gcsUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BulkUpload = ({ user, selectedProjectId }) => {
  const [step, setStep] = useState(1); // 1: Upload Excel, 2: Upload Files, 3: Review, 4: Processing
  const [excelFile, setExcelFile] = useState(null);
  const [parsedDocuments, setParsedDocuments] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/bulk-upload/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Excel file dropzone
  const onDropExcel = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setExcelFile(file);

    // Parse Excel file
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/bulk-upload/parse-excel`, formData);
      setParsedDocuments(response.data.documents);
      setParseErrors(response.data.errors);
      
      if (response.data.documents.length > 0) {
        toast.success(`Parsed ${response.data.documents.length} documents`);
        setStep(2);
      } else {
        toast.error('No valid documents found in Excel file');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    }
  }, []);

  const { getRootProps: getExcelRootProps, getInputProps: getExcelInputProps } = useDropzone({
    onDrop: onDropExcel,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  // v25: bulk upload native - dropzone now matches PDFs AND native files
  const onDropFiles = useCallback((acceptedFiles) => {
    setUploadedFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });

    // Update matched status for both PDF and native
    setParsedDocuments(docs =>
      docs.map(doc => ({
        ...doc,
        file_matched: acceptedFiles.some(f => f.name === doc.filename) || doc.file_matched,
        native_matched:
          (doc.native_filename && (acceptedFiles.some(f => f.name === doc.native_filename) || doc.native_matched))
          || doc.native_matched
          || false,
      }))
    );
  }, []);

  const { getRootProps: getFilesRootProps, getInputProps: getFilesInputProps } = useDropzone({
    onDrop: onDropFiles,
    accept: {
      'application/pdf': ['.pdf'],
      'application/octet-stream': ['.nwd'], /* v19: Navisworks */
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff']
    }
  });

  // Remove uploaded file
  const removeFile = (filename) => {
    setUploadedFiles(prev => prev.filter(f => f.name !== filename));
    setParsedDocuments(docs =>
      docs.map(doc => ({
        ...doc,
        file_matched: doc.filename === filename ? false : doc.file_matched,
        native_matched: doc.native_filename === filename ? false : doc.native_matched,
      }))
    );
  };

  // v25: native is optional - only count PDFs for unmatched
  const matchedCount = parsedDocuments.filter(d =>
    uploadedFiles.some(f => f.name === d.filename)
  ).length;

  const nativeRequested = parsedDocuments.filter(d => !!d.native_filename).length;
  const nativeMatchedCount = parsedDocuments.filter(d =>
    d.native_filename && uploadedFiles.some(f => f.name === d.native_filename)
  ).length;

  const unmatchedDocs = parsedDocuments.filter(d =>
    !uploadedFiles.some(f => f.name === d.filename)
  );

  const unmatchedNatives = parsedDocuments.filter(d =>
    d.native_filename && !uploadedFiles.some(f => f.name === d.native_filename)
  );

  // Process upload
  const handleProcessUpload = async () => {
    // v29: require listed natives
    if (unmatchedDocs.length > 0) {
      toast.error(`${unmatchedDocs.length} documents still missing PDF files`);
      return;
    }
    if (unmatchedNatives.length > 0) {
      const names = unmatchedNatives.map(d => d.native_filename).join(', ');
      toast.error(`${unmatchedNatives.length} native file(s) listed in Excel but not dropped: ${names}`);
      return;
    }

    setProcessing(true);
    setStep(4);
    setUploadProgress(0);

    try {
      // Step A: Upload each PDF directly to GCS via signed URL (bypasses Cloud Run 32MB limit).
      // We map by the original filename to attach gcs_path to the right metadata row.
      const fileByName = {};
      uploadedFiles.forEach((f) => { fileByName[f.name] = f; });

      // v25: upload PDF + (optional) native per row
      const docsWithGcs = [];
      const totalFiles = parsedDocuments.length || 1;
      for (let i = 0; i < parsedDocuments.length; i++) {
        const doc = parsedDocuments[i];
        const f = fileByName[doc.filename];
        if (!f) {
          throw new Error(`File missing for ${doc.document_number}: ${doc.filename}`);
        }
        const baseProgress = (i / totalFiles) * 100;
        const info = await uploadFileToGCS(f, 'reviews', (pct) => {
          setUploadProgress(Math.round(baseProgress + pct / totalFiles));
        });

        // Native (optional)
        let nativeInfo = null;
        if (doc.native_filename) {
          const nf = fileByName[doc.native_filename];
          if (nf) {
            nativeInfo = await uploadFileToGCS(nf, 'reviews', (pct) => {
              setUploadProgress(Math.round(baseProgress + pct / totalFiles));
            });
          }
        }

        docsWithGcs.push({
          document_number: doc.document_number,
          title: doc.title,
          revision: doc.revision || 'A',
          discipline: doc.discipline,
          discipline_code: doc.discipline_code,
          filename: doc.filename,
          description: doc.description,
          source_company: doc.source_company,
          document_type: doc.document_type,
          classification: doc.classification,
          revision_date: doc.revision_date,
          project_code: doc.project_code,
          notes: doc.notes,
          is_new_revision: !!doc.is_new_revision,
          existing_document_id: doc.existing_document_id || null,
          gcs_path: info.gcs_path,
          file_size: info.file_size,
          content_type: info.content_type,
          // v25: native file (optional)
          native_filename: doc.native_filename || null,
          native_gcs_path: nativeInfo ? nativeInfo.gcs_path : null,
          native_file_size: nativeInfo ? nativeInfo.file_size : null,
          native_content_type: nativeInfo ? nativeInfo.content_type : null,
        });
      }
      setUploadProgress(100);

      // Step B: tell backend to register all the GCS-uploaded files
      const response = await axios.post(`${API}/bulk-upload/process-gcs`, {
        documents: docsWithGcs,
        project_id: selectedProjectId || null,
      });

      setResults(response.data);
      toast.success(`Successfully uploaded ${response.data.success.length} documents`);
      
      if (response.data.failed.length > 0) {
        toast.warning(`${response.data.failed.length} documents failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Upload failed');
      setStep(3);
    } finally {
      setProcessing(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setStep(1);
    setExcelFile(null);
    setParsedDocuments([]);
    setParseErrors([]);
    setUploadedFiles([]);
    setResults(null);
    setUploadProgress(0);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bulk Document Upload</h1>
        <p className="text-gray-500 mt-1">Upload multiple documents at once using an Excel template</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 px-4">
        {[
          { num: 1, label: 'Upload Excel' },
          { num: 2, label: 'Upload Files' },
          { num: 3, label: 'Review' },
          { num: 4, label: 'Complete' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s.num 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className={`mt-2 text-sm ${step >= s.num ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                {s.label}
              </span>
            </div>
            {i < 3 && (
              <div className={`flex-1 h-1 mx-4 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload Excel */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Step 1: Upload Excel Template
            </CardTitle>
            <CardDescription>
              Download the template, fill in your document metadata, and upload it here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleDownloadTemplate} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Download Excel Template
            </Button>

            <div
              {...getExcelRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <input {...getExcelInputProps()} />
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Drag & drop your filled Excel template here</p>
              <p className="text-sm text-gray-400 mt-1">or click to select file</p>
            </div>

            {excelFile && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700">{excelFile.name}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload Files */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Step 2: Upload Document Files
            </CardTitle>
            <CardDescription>
              Upload the PDF files that match the filenames in your Excel template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Parse errors */}
            {parseErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">{parseErrors.length} rows had errors:</p>
                  <ul className="list-disc list-inside text-sm">
                    {parseErrors.slice(0, 5).map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                    {parseErrors.length > 5 && <li>...and {parseErrors.length - 5} more</li>}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Required files list */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-2">Required Files ({matchedCount}/{parsedDocuments.length} matched)</h3>
              <div className="flex flex-wrap gap-2">
                {parsedDocuments.map((doc, i) => {
                  const isMatched = uploadedFiles.some(f => f.name === doc.filename);
                  return (
                    <Badge 
                      key={i} 
                      variant={isMatched ? "default" : "outline"}
                      className={isMatched ? "bg-green-600" : "border-orange-400 text-orange-600"}
                    >
                      {isMatched && <CheckCircle className="w-3 h-3 mr-1" />}
                      {doc.filename}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Drop zone */}
            <div
              {...getFilesRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <input {...getFilesInputProps()} />
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Drag & drop your PDF files here</p>
              <p className="text-sm text-gray-400 mt-1">or click to select multiple files</p>
            </div>

            {/* Uploaded files list */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium">Uploaded Files ({uploadedFiles.length})</h3>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {uploadedFiles.map((file, i) => {
                    const isRequired = parsedDocuments.some(d => d.filename === file.name);
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-500" />
                          <span className="text-sm">{file.name}</span>
                          {isRequired ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">Match</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">Extra</Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.name)}
                        >
                          <Trash className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={matchedCount === 0}
                className="gap-2"
              >
                Review <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 3: Review & Confirm</CardTitle>
            <CardDescription>
              Review the documents before uploading. All documents will have a review cycle created automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {unmatchedDocs.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {unmatchedDocs.length} documents are missing files: {unmatchedDocs.map(d => d.filename).join(', ')}
                </AlertDescription>
              </Alert>
            )}

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Document Number</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Revision</TableHead>
                    <TableHead>Discipline</TableHead>
                    <TableHead>Disc. Code</TableHead>
                    <TableHead>Classification</TableHead>
                    <TableHead>Revision Date</TableHead>
                    <TableHead>Source Company</TableHead>
                    <TableHead>Project Code</TableHead>
                    <TableHead>PDF Filename</TableHead>
                    <TableHead>Native File</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedDocuments.map((doc, i) => {
                    const hasFile = uploadedFiles.some(f => f.name === doc.filename);
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          {hasFile ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{doc.document_number}</TableCell>
                        <TableCell>{doc.title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{doc.revision}</span>
                            {doc.is_new_revision && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                                New revision
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{doc.discipline || '-'}</TableCell>
                        <TableCell>{doc.discipline_code || '-'}</TableCell>
                        <TableCell>{doc.classification || '-'}</TableCell>
                        <TableCell>{doc.revision_date || '-'}</TableCell>
                        <TableCell>{doc.source_company || '-'}</TableCell>
                        <TableCell>{doc.project_code || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-500">{doc.filename}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {doc.native_filename ? (
                            <span className={uploadedFiles.some(f => f.name === doc.native_filename) ? "text-green-700" : "text-amber-600"}>
                              {doc.native_filename}
                              {!uploadedFiles.some(f => f.name === doc.native_filename) && " (missing)"}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button 
                onClick={handleProcessUpload} 
                disabled={unmatchedDocs.length > 0 || unmatchedNatives.length > 0 || processing}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {parsedDocuments.length} Documents
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Processing / Results */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {processing ? 'Processing Upload...' : 'Upload Complete'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {processing && (
              <div className="space-y-2">
                <Progress value={uploadProgress} className="h-3" />
                <p className="text-center text-sm text-gray-500">{uploadProgress}% complete</p>
              </div>
            )}

            {results && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700">{results.success.length}</p>
                    <p className="text-sm text-green-600">Successful</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-600">{results.failed.length}</p>
                    <p className="text-sm text-red-500">Failed</p>
                  </div>
                </div>

                {results.failed.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="font-medium text-red-700 mb-2">Failed Uploads:</h3>
                    <ul className="space-y-1 text-sm text-red-600">
                      {results.failed.map((f, i) => (
                        <li key={i}>{f.document_number}: {f.error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {results.success.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-medium text-green-700 mb-2">Successfully Uploaded:</h3>
                    <div className="max-h-48 overflow-y-auto space-y-1 text-sm text-green-600">
                      {results.success.map((s, i) => (
                        <div key={i}>{s.document_number} - {s.title}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4 pt-4">
                  <Button variant="outline" onClick={handleReset}>
                    Upload More Documents
                  </Button>
                  <Button onClick={() => window.location.href = '/documents'}>
                    Go to Documents
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkUpload;
